import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Input,
  Label,
  Skeleton,
} from '@sea-snag/ui';
import { HTTPError } from 'ky';
import { useState } from 'react';
import { toast } from 'sonner';

import { useAdminCompanyDeletionPreview, useDeleteAdminCompany } from '../hooks/use-admin';

interface DeleteCompanyDialogProps {
  company: { pid: string; name: string } | null;
  onOpenChange: (open: boolean) => void;
}

async function extractErrorMessage(error: unknown): Promise<string> {
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.json()) as { error?: { message?: string } };
      if (body?.error?.message) return body.error.message;
    } catch {
      // ignore parse errors
    }
  }
  return 'Failed to delete company';
}

export function DeleteCompanyDialog({ company, onOpenChange }: DeleteCompanyDialogProps) {
  const [confirmation, setConfirmation] = useState('');
  // Keep showing the last company while the close animation runs.
  const [shown, setShown] = useState(company);
  if (company && company !== shown) setShown(company);

  const preview = useAdminCompanyDeletionPreview(company?.pid ?? null);
  const deleteCompany = useDeleteAdminCompany();

  const data = preview.data;
  const blockers = data?.blockers;
  const isBlocked =
    !!blockers && (blockers.superAdmins.length > 0 || blockers.sharedUsers.length > 0);
  const canDelete = !!data && !isBlocked && confirmation === data.name && !deleteCompany.isPending;

  function handleOpenChange(open: boolean) {
    if (deleteCompany.isPending) return;
    if (!open) setConfirmation('');
    onOpenChange(open);
  }

  function handleDelete() {
    if (!company || !canDelete) return;
    deleteCompany.mutate(company.pid, {
      onSuccess: () => {
        toast.success(`Deleted ${company.name}`);
        setConfirmation('');
        onOpenChange(false);
      },
      onError: (error) => {
        void extractErrorMessage(error).then((message) => toast.error(message));
        // Blockers may have appeared since the preview loaded.
        void preview.refetch();
      },
    });
  }

  const counts = data
    ? ([
        ['users', data.counts.users],
        ['memberships', data.counts.members],
        ['invitations', data.counts.invitations],
        ['competitor sets', data.counts.compSets],
        ['alert rules', data.counts.alertRules],
        ['integrations', data.counts.integrations],
        ['boats', data.counts.boats],
      ] as const)
    : [];

  return (
    <AlertDialog open={company !== null} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {shown?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the company and all of its data. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {preview.isLoading && <Skeleton className="h-32 w-full" />}

        {preview.isError && (
          <Alert variant="destructive">
            <AlertDescription>Could not load what would be deleted.</AlertDescription>
          </Alert>
        )}

        {data && isBlocked && blockers && (
          <Alert variant="destructive">
            <AlertTitle>This company can't be deleted</AlertTitle>
            <AlertDescription>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {blockers.superAdmins.map((u) => (
                  <li key={`sa-${u.email}`}>
                    {u.email} is a super admin — move or demote them first.
                  </li>
                ))}
                {blockers.sharedUsers.map((u) => (
                  <li key={`shared-${u.email}-${u.otherCompanyName}`}>
                    {u.email} also belongs to {u.otherCompanyName}.
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {data && !isBlocked && (
          <div className="space-y-4">
            <div className="rounded-md border p-3 text-sm">
              <p className="mb-2 font-medium">The following will be deleted:</p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                {counts.map(([label, value]) => (
                  <li key={label}>
                    <span className="font-medium text-foreground">{value}</span> {label}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-company-confirmation">
                Type <span className="font-semibold">{data.name}</span> to confirm
              </Label>
              <Input
                id="delete-company-confirmation"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
                disabled={deleteCompany.isPending}
              />
            </div>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteCompany.isPending}>Cancel</AlertDialogCancel>
          {!isBlocked && (
            <Button variant="destructive" disabled={!canDelete} onClick={handleDelete}>
              {deleteCompany.isPending ? 'Deleting…' : 'Delete company'}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
